import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const steps = [
  { icon: 'ri-phone-line', title: 'Emergency Call', desc: 'Call us 24/7 at (209) 451-2288. We dispatch a crew immediately to secure and assess your property.' },
  { icon: 'ri-search-eye-line', title: 'Damage Assessment', desc: 'We document all fire, smoke, and soot damage for insurance purposes and create a restoration plan.' },
  { icon: 'ri-shield-line', title: 'Board-Up & Tarping', desc: 'We secure your property with board-up services and roof tarping to prevent further damage.' },
  { icon: 'ri-delete-bin-line', title: 'Debris Removal', desc: 'Charred materials and unsalvageable items are safely removed and disposed of properly.' },
  { icon: 'ri-wind-line', title: 'Smoke & Soot Removal', desc: 'Specialized equipment and cleaning agents eliminate smoke odor and soot from all surfaces.' },
  { icon: 'ri-home-smile-line', title: 'Full Restoration', desc: 'We rebuild and restore your property to its pre-fire condition, handling everything from drywall to painting.' },
];

const reasons = [
  { icon: 'ri-time-line', title: '24/7 Emergency Response', desc: 'Fire damage worsens by the hour. We respond immediately to minimize losses.' },
  { icon: 'ri-award-line', title: 'IICRC Certified', desc: 'Our technicians are certified in fire and smoke damage restoration best practices.' },
  { icon: 'ri-file-list-3-line', title: 'Insurance Coordination', desc: 'We work directly with your insurance adjuster to ensure a smooth, fair claims process.' },
  { icon: 'ri-tools-line', title: 'Specialized Equipment', desc: 'Ozone generators, thermal foggers, and HEPA air scrubbers for complete odor elimination.' },
  { icon: 'ri-map-pin-line', title: 'Local Merced Experts', desc: 'We know Merced and the Central Valley — trusted by local homeowners and businesses.' },
  { icon: 'ri-thumb-up-line', title: 'Complete Rebuild Capability', desc: 'From cleanup to full reconstruction, we handle every phase under one roof.' },
];

export default function FireDamagePage() {
  useEffect(() => {
    const siteUrl = 'https://mercedwaterrestoration.com';

    document.title = 'Fire Damage Restoration Merced CA | Smoke & Soot Cleanup | Indoor-Restore';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Complete fire & smoke damage cleanup, odor elimination, board-up services, and full reconstruction in Merced, CA. IICRC certified. 24/7 emergency response. Call (209) 451-2288.');

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'fire damage restoration Merced CA, smoke damage cleanup, soot removal, fire damage repair Central Valley, smoke odor elimination Merced');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/services/fire-damage`);

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Fire Damage Restoration",
      "name": "Fire Damage Restoration in Merced, CA",
      "description": "Complete fire and smoke damage cleanup, odor elimination, board-up services, and full reconstruction after fire events in Merced County. 24/7 emergency response.",
      "url": `${siteUrl}/services/fire-damage`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": { "@type": "State", "name": "California" }
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "servicePhone": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Emergency Service",
          "availableLanguage": "English",
          "hoursAvailable": "24/7"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5d0a41a0b645aa586aecdc3d605bc016.jpg"
            alt="Fire Damage Restoration Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto py-24">
          <span className="inline-block bg-orange-500/30 border border-orange-400/50 text-orange-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            24/7 Emergency Service
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Fire Damage Restoration<br />in Merced, CA
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            From smoke cleanup to full structural rebuilds — Indoor-Restore Services restores your property after fire damage, fast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call (209) 451-2288
            </a>
            <a href="/#contact" className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">Comprehensive Fire & Smoke Damage Cleanup</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                A fire leaves behind more than visible damage. Smoke, soot, and toxic residues penetrate deep into walls, ceilings, and HVAC systems — causing ongoing health risks and structural deterioration if not properly addressed.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                <strong>Indoor-Restore Services</strong> provides complete fire damage restoration in Merced, CA — from emergency board-up and debris removal to smoke odor elimination and full structural reconstruction.
              </p>
              <ul className="space-y-3">
                {[
                  'Smoke & soot removal from all surfaces',
                  'Odor elimination with ozone & thermal fogging',
                  'Structural drying after firefighting water',
                  'Content cleaning & pack-out services',
                  'Board-up & emergency tarping',
                  'Full interior reconstruction',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-700 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <img
                src="https://readdy.ai/api/search-image?query=restoration%20technician%20in%20protective%20gear%20cleaning%20smoke%20damaged%20walls%20with%20professional%20cleaning%20equipment%20inside%20a%20fire%20damaged%20room%20with%20soot%20covered%20surfaces%2C%20bright%20work%20lights%2C%20clean%20simple%20background&width=800&height=600&seq=fd-overview-02&orientation=landscape"
                alt="Fire damage cleanup and smoke removal"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Fire Damage Restoration Process</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">A thorough, step-by-step approach to safely restore your home or business after a fire.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <span className="text-3xl font-bold text-teal-100">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Indoor-Restore Services?</h2>
            <p className="text-white/80 max-w-2xl mx-auto">When fire strikes, you need a team that moves fast and handles everything — from cleanup to complete restoration.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reasons.map((r, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-7 border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5">
                  <i className={`${r.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{r.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Experienced Fire Damage? Call Us Now.</h2>
          <p className="text-gray-500 mb-8 text-lg">Our team is standing by 24/7 to respond to fire emergencies across Merced County and the Central Valley.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
